import { useEffect, useId, useState } from 'react';
import { api, Alert, ErrorAlert, Icon, Spinner, useAsyncAction } from '@pryme/ui';
import { Link } from 'react-router-dom';
import { useDev } from '../lib/context';
import { coveringDomain, verifiedOrgDomains, type Service } from '../lib/types';

/** Client-side mirror of the API's validateRedirect() so developers get instant feedback. */
export function validateRedirectUri(uri: string, service: Service, orgDomains: string[]): string | null {
  let u: URL;
  try {
    u = new URL(uri);
  } catch {
    return 'Not a valid absolute URL.';
  }
  if (u.hash) return 'Fragments (#…) are not allowed.';
  if (u.username || u.password) return 'Credentials in URLs are not allowed.';
  if (uri.includes('*')) return 'Wildcards are not allowed: every URI must match exactly.';
  const local = u.hostname === 'localhost' || u.hostname === '127.0.0.1';
  if (u.protocol !== 'https:') {
    if (u.protocol === 'http:' && local) {
      if (service.environment !== 'sandbox') return 'http://localhost is allowed in sandbox services only.';
    } else return 'Must use HTTPS (http://localhost is allowed in sandbox only).';
  }
  if (service.environment === 'production' && !coveringDomain(u.hostname, orgDomains)) {
    return `${u.hostname} is not on a verified domain of your organization.`;
  }
  return null;
}

export function RedirectEditor(props: { service: Service; canEdit: boolean; onSaved(uris: string[]): void }) {
  const id = useId();
  const [text, setText] = useState(props.service.redirectUris.join('\n'));
  const save = useAsyncAction();
  const [saved, setSaved] = useState(false);
  const { orgDomains } = useDev();
  const verified = verifiedOrgDomains(orgDomains);
  const production = props.service.environment === 'production';
  useEffect(() => setText(props.service.redirectUris.join('\n')), [props.service.redirectUris]);

  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const issues = lines.map((l) => ({ uri: l, error: validateRedirectUri(l, props.service, verified) }));
  const invalid = issues.filter((i) => i.error);

  const submit = async () => {
    setSaved(false);
    const r = await save.run(() => api<{ redirectUris: string[] }>(`/v1/services/${props.service.id}/redirects`, { method: 'PUT', body: { redirectUris: lines } }));
    if (r) {
      setSaved(true);
      props.onSaved(r.redirectUris);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="p-field">
        <label htmlFor={id}>Allowed redirect URIs (one per line, max 10)</label>
        <textarea
          id={id}
          className="p-textarea"
          rows={4}
          value={text}
          disabled={!props.canEdit}
          onChange={(e) => {
            setText(e.target.value);
            setSaved(false);
          }}
          placeholder={production ? `https://${verified[0] ?? 'example.com'}/auth/pryme/callback` : 'http://localhost:3000/auth/pryme/callback'}
          aria-describedby={`${id}-hint`}
          aria-invalid={invalid.length > 0}
        />
        <span className="p-hint" id={`${id}-hint`}>
          Exact match only (scheme, host, port, path and query). No wildcards, fragments or credentials. {production
            ? 'Production: HTTPS on a verified domain of your organization or a subdomain of one.'
            : 'Sandbox: any HTTPS URL, plus http://localhost and http://127.0.0.1 for local development. No domain needed.'}
        </span>
      </div>
      {production && verified.length === 0 && (
        <Alert tone="warn">
          Your organization has no verified domain yet. Production redirect URIs must be on one: <Link to="/domains">verify a domain</Link> first.
        </Alert>
      )}
      {lines.length > 0 && (
        <ul className="dv-checks p-small" aria-label="Validation" style={{ marginBottom: '1rem' }}>
          {issues.map((i) => (
            <li key={i.uri} className={i.error ? 'bad' : 'ok'}>
              <Icon name={i.error ? 'x' : 'check'} size={15} label={i.error ? 'Invalid' : 'Valid'} />
              <span><code>{i.uri}</code>{i.error && <span style={{ color: 'var(--p-bad)' }}> · {i.error}</span>}</span>
            </li>
          ))}
        </ul>
      )}
      <ErrorAlert error={save.error} />
      {saved && <Alert tone="ok">Saved {lines.length} redirect URI{lines.length === 1 ? '' : 's'}.</Alert>}
      {props.canEdit && (
        <button className="p-btn" disabled={save.busy || invalid.length > 0 || lines.length > 10}>
          {save.busy ? <Spinner label="Saving…" /> : 'Save redirect URIs'}
        </button>
      )}
    </form>
  );
}
